// React
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Refine
import { useTranslation } from '@refinedev/core';

// Mantine
import {
  Group,
  Loader,
  Select,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

// Resources
import {
  useGetIntakeItem,
  usePaginateIntakeItemsByProvider,
  usePaginateIntakeItemsBySite,
} from 'api/resource';

// Components
import { useActiveForm } from 'pages/claims/components/ClaimFormProvider';

// Utils
import { getValueByPath } from 'utils/getValueByPath';
import {
  appendUniqueItemsByID,
  dedupeItemsByID,
  hasSameItemIDs,
  matchesUploadSearch,
  passthroughOptionsFilter,
  UPLOAD_LINK_PAGE_SIZE,
  UploadSelectOption,
  upsertUploadOption,
} from 'utils/uploadLinkSelectUtils';
import { IntakeItem } from '@billing/validation';

interface ModalUploadLinkInputProps {
  label?: string;
  accessorKey?: string;
  required?: boolean;
  disabled?: boolean;
}

const EMPTY_INTAKE_ITEMS: IntakeItem[] = [];
const LOADING_MORE_OPTION_VALUE = '__upload_link_loading_more__';

export const ModalUploadLinkInput: React.FC<ModalUploadLinkInputProps> = ({
  label,
  accessorKey = 'intakeItemID',
  required = false,
  disabled = false,
}) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';
  const { form, prefix, formType, siteID: bulkEditSiteID } = useActiveForm();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue.trim(), 300);
  const [page, setPage] = useState(0);
  const [pagedItems, setPagedItems] = useState<IntakeItem[]>([]);
  // Keep a rolling cache of items we've seen so local search can still match
  // results that may no longer be in the currently loaded page window.
  const seenItemsByIDRef = useRef<Map<string, IntakeItem>>(new Map());

  const intakeItemPath = prefix ? `${prefix}.${accessorKey}` : accessorKey;
  const siteIDPath = prefix ? `${prefix}.siteID` : 'siteID';
  const providerIDPath = prefix ? `${prefix}.providerID` : 'providerID';

  const selectedIntakeItemID =
    (getValueByPath(form.getValues(), intakeItemPath) as string | null) || null;
  const siteID = (() => {
    if (formType === 'bulkEdit') {
      if (!bulkEditSiteID) {
        throw new Error('Bulk edit upload selection requires a site ID');
      }
      return bulkEditSiteID;
    }

    return (getValueByPath(form.getValues(), siteIDPath) as string) || '';
  })();
  const providerID =
    formType === 'bulkEdit'
      ? null
      : (getValueByPath(form.getValues(), providerIDPath) as string | null) ||
        null;

  const isProviderScopedSearch = !!providerID;
  const siteQuerySiteID = isProviderScopedSearch ? '' : siteID;
  const providerQueryProviderID = isProviderScopedSearch ? providerID : '';
  const searchScopeKey = `${isProviderScopedSearch ? 'provider' : 'site'}:${
    providerQueryProviderID || ''
  }:${siteID || ''}`;

  useEffect(() => {
    // Scope changes (site/provider) represent a new dataset, so clear all
    // pagination/search carry-over to avoid showing stale upload options.
    seenItemsByIDRef.current.clear();
    setPage(0);
    setPagedItems([]);
  }, [searchScopeKey]);

  useEffect(() => {
    // A new search term should restart pagination from page 0.
    setPage(0);
    setPagedItems([]);
  }, [debouncedSearch]);

  const siteQuery = usePaginateIntakeItemsBySite({
    siteID: siteQuerySiteID,
    page,
    pageSize: UPLOAD_LINK_PAGE_SIZE,
    globalFilter: debouncedSearch,
  });

  const providerQuery = usePaginateIntakeItemsByProvider({
    providerID: providerQueryProviderID,
    siteID: siteID,
    page,
    pageSize: UPLOAD_LINK_PAGE_SIZE,
    globalFilter: debouncedSearch,
  });

  const selectedUploadQuery = useGetIntakeItem({
    id: selectedIntakeItemID,
  });

  const activeQuery = isProviderScopedSearch ? providerQuery : siteQuery;
  const intakeItems = activeQuery.data?.data ?? EMPTY_INTAKE_ITEMS;
  // Before page > 0 loads, show page-0 server results directly.
  const loadedItems = pagedItems.length > 0 ? pagedItems : intakeItems;
  const totalAvailable = activeQuery.data?.total || 0;
  const hasMore = loadedItems.length < totalAvailable;
  const isLoadingNextPage = activeQuery.isFetching && page > 0;
  const selectedUpload = selectedUploadQuery.data;

  useEffect(() => {
    if (activeQuery.isPlaceholderData) return;

    if (page === 0) {
      // Replace with a deduped base page so page resets don't accumulate.
      const next = dedupeItemsByID(intakeItems);
      setPagedItems((current) =>
        hasSameItemIDs(current, next) ? current : next
      );
      return;
    }

    // For subsequent pages, append new IDs and update existing entries by ID.
    setPagedItems((current) => appendUniqueItemsByID(current, intakeItems));
  }, [activeQuery.isPlaceholderData, intakeItems, page]);

  useEffect(() => {
    pagedItems.forEach((item) => {
      seenItemsByIDRef.current.set(item.id, item);
    });

    // Ensure a pre-selected upload is renderable even if it isn't in current page data.
    if (selectedUpload?.id) {
      seenItemsByIDRef.current.set(selectedUpload.id, selectedUpload);
    }
  }, [pagedItems, selectedUpload]);

  const { selectData, optionProviderByID } = useMemo(() => {
    const optionsByID = new Map<string, UploadSelectOption>();
    const providerByID = new Map<string, string>();

    loadedItems.forEach((item) => {
      upsertUploadOption({
        item,
        translate,
        optionsByID,
        providerByID,
      });
    });

    if (selectedUpload?.id && !optionsByID.has(selectedUpload.id)) {
      upsertUploadOption({
        item: selectedUpload,
        translate,
        optionsByID,
        providerByID,
      });
    }

    if (debouncedSearch.trim()) {
      seenItemsByIDRef.current.forEach((item) => {
        if (
          matchesUploadSearch(item, debouncedSearch, translate) &&
          !optionsByID.has(item.id)
        ) {
          // Include cache hits so users can still find previously seen uploads
          // while the server-side paged query catches up.
          upsertUploadOption({
            item,
            translate,
            optionsByID,
            providerByID,
          });
        }
      });
    }

    if (isLoadingNextPage && hasMore) {
      // Sentinel option keeps the menu open and communicates infinite-scroll progress.
      optionsByID.set(LOADING_MORE_OPTION_VALUE, {
        value: LOADING_MORE_OPTION_VALUE,
        label: translate(
          'pages:claims.table.columns.linkedUpload.loadingMore',
          'Loading more uploads...'
        ),
        disabled: true,
      });
    }

    return {
      selectData: Array.from(optionsByID.values()),
      optionProviderByID: providerByID,
    };
  }, [
    loadedItems,
    debouncedSearch,
    hasMore,
    isLoadingNextPage,
    selectedUpload,
    translate,
  ]);

  const loading =
    activeQuery.isLoading ||
    (activeQuery.isFetching && page === 0) ||
    selectedUploadQuery.isLoading;
  const disabledBecauseNoSite = !siteID;
  const effectiveDisabled = disabled || disabledBecauseNoSite;
  const resolvedLabel =
    label ||
    translate(
      'pages:claims.table.columns.linkedUpload.header',
      'Linked Upload'
    );

  return (
    <Select
      key={intakeItemPath}
      label={resolvedLabel}
      radius="md"
      size="md"
      searchable
      clearable
      allowDeselect
      withAsterisk={required}
      c={color}
      value={selectedIntakeItemID}
      data={selectData}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onChange={(value) => {
        if (value === LOADING_MORE_OPTION_VALUE) return;
        form.setFieldValue(
          intakeItemPath,
          value || (formType === 'bulkEdit' ? undefined : null)
        );
      }}
      error={form.getInputProps(intakeItemPath).error}
      placeholder={
        disabledBecauseNoSite
          ? translate(
              'pages:claims.table.columns.linkedUpload.selectSiteFirst',
              'Select a site first'
            )
          : translate(
              'pages:claims.table.columns.linkedUpload.searchDescription',
              'Search by upload #, upload ID, or provider'
            )
      }
      renderOption={({ option }) => {
        if (option.value === LOADING_MORE_OPTION_VALUE) {
          return (
            <Group gap={8} wrap="nowrap">
              <Loader size={14} />
              <Text size="sm">{option.label}</Text>
            </Group>
          );
        }

        const providerName = optionProviderByID.get(option.value);
        return (
          <Text size="sm">
            {providerName ? `${option.label} | ${providerName}` : option.label}
          </Text>
        );
      }}
      filter={passthroughOptionsFilter}
      scrollAreaProps={{
        onBottomReached: () => {
          if (effectiveDisabled || !hasMore || activeQuery.isFetching) return;
          setPage((current) => current + 1);
        },
      }}
      nothingFoundMessage={
        loading
          ? translate(
              'pages:claims.table.columns.linkedUpload.searching',
              'Searching uploads...'
            )
          : translate(
              'pages:claims.table.columns.linkedUpload.noUploadsFound',
              'No uploads found'
            )
      }
      rightSection={loading ? <Loader size={16} /> : null}
      disabled={effectiveDisabled}
      maxDropdownHeight={320}
      comboboxProps={{ withinPortal: true }}
      data-testid="upload-link-select"
    />
  );
};
